/**
 * Consignment report API client (Reports → Weekly Consignment). Talks to
 * mttr/app_api `/api/reports/consignment*`. All authenticated roles;
 * scoping happens server-side.
 *
 * Three endpoints:
 *   GET  /api/reports/consignment                          — preview JSON
 *   GET  /api/reports/consignment.csv?...                  — flat CSV (Blob)
 *   GET  /api/reports/consignment.zip?...                  — manifest + per-file (Blob)
 *   GET  /api/reports/consignment/file/{stem}?format=json  — one rendered file (Blob)
 *   GET  /api/reports/consignment/file/{stem}?format=csv   — one invoice as CSV
 */

import { ApiError, apiBase, apiFetch } from "./apiClient";

export interface ConsignmentInvoiceLine {
  productName: string;
  partNo: string;
  custpart: string;
  vendor: string;
  companyNameAbbr: string;
  price: string;
  qty: string;
  total: string;
  jobNo: string;
  type: string;
}

export interface ConsignmentInvoicePayload {
  headerDescription: string;
  docNum: string;
  startTime: string;
  endTime: string;
  total: string;
  costCenter: string;
  companyName: string;
  smartbox: string;
  requester: string;
  deliverToLoc: string;
  list: ConsignmentInvoiceLine[];
}

export interface ConsignmentInvoicePreview {
  kind: "CONSIGNMENT" | "RETURN";
  file_name_stem: string;
  doc_num: string;
  smartbox: string;
  smartbox_id: number;
  vendor: string;
  vendor_company_id: number;
  customer_company_id: number;
  cost_center: string;
  deliver_to_loc: string;
  requester: string;
  line_count: number;
  total: string;
  payload: ConsignmentInvoicePayload;
}

export interface ConsignmentMeta {
  customer_id: number;
  tz: string;
  cutoff_dow: number;
  cutoff_hour: number;
  default_type: string;
  period_start_utc: string;
  period_end_utc: string;
  event_count: number;
  invoice_count: number;
  vendor_pin: number | null;
  smartbox_id: number | null;
  orphans: {
    no_smartbox: number;
    no_vendor: number;
    no_customer: number;
    no_item: number;
    no_price: number;
  };
}

export interface ConsignmentPreview {
  meta: ConsignmentMeta;
  invoices: ConsignmentInvoicePreview[];
}

export interface ConsignmentRequester {
  customer_company_id: number;
  customer_name: string | null;
  workcenter_id: number;
  workcenter_code: string | null;
  workcenter_name: string | null;
  requester_id: string;
  note: string | null;
  set_by: string;
  set_utc: string | null;
}

export class ConsignmentApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "ConsignmentApiError";
  }
}

function buildQs(opts: {
  customer?: number;
  vendor?: number;
  smartbox?: number;
  week?: string;
  weeksBack?: number;
}): string {
  const p = new URLSearchParams();
  if (opts.customer != null) p.set("customer", String(opts.customer));
  if (opts.vendor != null) p.set("vendor", String(opts.vendor));
  if (opts.smartbox != null) p.set("smartbox", String(opts.smartbox));
  if (opts.week) p.set("week", opts.week);
  if (opts.weeksBack != null) p.set("weeks_back", String(opts.weeksBack));
  return p.toString();
}

async function getJson<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  try {
    return await apiFetch<T>(path, token, init);
  } catch (e) {
    throw e instanceof ApiError ? new ConsignmentApiError(e.message, e.status) : e;
  }
}

async function getBlob(
  path: string, token: string, accept: string,
): Promise<{ blob: Blob; filename: string | null }> {
  const resp = await fetch(`${apiBase()}${path}`, {
    headers: { Accept: accept, "access-token": token },
    cache: "no-store",
  });
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const j = await resp.json();
      if (j && typeof j.error === "string") detail = j.error;
    } catch {
      /* fall through */
    }
    throw new ConsignmentApiError(detail, resp.status);
  }
  const disposition = resp.headers.get("Content-Disposition") || "";
  const m = /filename="?([^";]+)"?/.exec(disposition);
  return { blob: await resp.blob(), filename: m ? m[1] : null };
}

export function fetchConsignmentPreview(
  token: string,
  opts: {
    customer?: number; vendor?: number; smartbox?: number;
    week?: string; weeksBack?: number;
  } = {},
): Promise<ConsignmentPreview> {
  const qs = buildQs(opts);
  return getJson<ConsignmentPreview>(
    `/api/reports/consignment${qs ? `?${qs}` : ""}`, token,
  );
}

export function fetchConsignmentCsv(
  token: string,
  opts: {
    customer?: number; vendor?: number; smartbox?: number;
    week?: string; weeksBack?: number;
  } = {},
): Promise<{ blob: Blob; filename: string | null }> {
  const qs = buildQs(opts);
  return getBlob(
    `/api/reports/consignment.csv${qs ? `?${qs}` : ""}`,
    token, "text/csv",
  );
}

export function fetchConsignmentZip(
  token: string,
  opts: {
    customer?: number; vendor?: number; smartbox?: number;
    week?: string; weeksBack?: number;
  } = {},
): Promise<{ blob: Blob; filename: string | null }> {
  const qs = buildQs(opts);
  return getBlob(
    `/api/reports/consignment.zip${qs ? `?${qs}` : ""}`,
    token, "application/zip",
  );
}

export function fetchConsignmentPdf(
  token: string,
  opts: {
    customer?: number; vendor?: number; smartbox?: number;
    week?: string; weeksBack?: number;
  } = {},
): Promise<{ blob: Blob; filename: string | null }> {
  const qs = buildQs(opts);
  return getBlob(
    `/api/reports/consignment.pdf${qs ? `?${qs}` : ""}`,
    token, "application/pdf",
  );
}

export function fetchConsignmentFile(
  token: string,
  fileStem: string,
  fmt: "json" | "csv" | "pdf",
  opts: {
    customer?: number; vendor?: number; smartbox?: number;
    week?: string; weeksBack?: number;
  } = {},
): Promise<{ blob: Blob; filename: string | null }> {
  const qs = buildQs(opts);
  const sep = qs ? "&" : "?";
  const accept =
    fmt === "csv" ? "text/csv" :
    fmt === "pdf" ? "application/pdf" :
    "application/json";
  return getBlob(
    `/api/reports/consignment/file/${encodeURIComponent(fileStem)}${qs ? `?${qs}` : ""}${sep}format=${fmt}`.replace(
      /\?&/, "?",
    ),
    token, accept,
  );
}

/** Trigger a Blob download in the browser. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------- Requesters admin (platform-only) ----------

export function listConsignmentRequesters(
  token: string, customerId?: number,
): Promise<{ total: number; requesters: ConsignmentRequester[] }> {
  const qs = customerId != null ? `?customer=${customerId}` : "";
  return getJson(`/api/reports/consignment/requesters${qs}`, token);
}

export async function upsertConsignmentRequester(
  token: string,
  body: {
    customer_company_id: number;
    workcenter_id: number;
    requester_id: string;
    note?: string;
  },
): Promise<void> {
  await getJson<unknown>("/api/reports/consignment/requesters", token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function deleteConsignmentRequester(
  token: string, customerId: number, workcenterId: number,
): Promise<void> {
  await getJson<unknown>(
    `/api/reports/consignment/requesters?customer=${customerId}&workcenter=${workcenterId}`,
    token,
    { method: "DELETE" },
  );
}
